<template>
  <div class="dashboard">
    <AppNavbar />
    
    <div class="dashboard-content">
      <div class="dashboard-header">
        <h1>Dashboard</h1>
        <div class="last-updated">
          Last updated: {{ formattedDate }}
          <button class="refresh-button" @click="refreshData">
            <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M23 4v6h-6"></path>
              <path d="M1 20v-6h6"></path>
              <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10"></path>
              <path d="M1 14l4.64 4.36A9 9 0 0 0 20.49 15"></path>
            </svg>
          </button>
        </div>
      </div>
      
      <div class="portfolio-summary">
        <div class="summary-card total-balance">
          <h2>Total Balance</h2>
          <div class="balance-amount">{{ formatUSD(totalBalance) }}</div>
          <div 
            class="balance-change" 
            :class="{ 
              'positive': portfolioChange > 0, 
              'negative': portfolioChange < 0 
            }"
          >
            {{ formatPercentage(portfolioChange) }} (24h)
          </div>
        </div>
        
        <div class="summary-card asset-allocation">
          <h2>Asset Allocation</h2>
          <div class="allocation-chart">
            <apexchart
              type="donut"
              height="200"
              :options="pieChartOptions"
              :series="assetValues"
            ></apexchart>
          </div>
        </div>
      </div>
      
      <div class="dashboard-row">
        <div class="dashboard-column">
          <div class="section-card">
            <div class="section-header">
              <h2>Portfolio Value</h2>
              <div class="period-selector">
                <button 
                  v-for="period in periods" 
                  :key="period"
                  class="period-button"
                  :class="{ active: selectedPeriod === period }"
                  @click="selectedPeriod = period"
                >
                  {{ period }}
                </button>
              </div>
            </div>
            
            <div class="portfolio-chart">
              <apexchart
                type="area"
                height="280"
                :options="areaChartOptions"
                :series="areaSeries"
              ></apexchart>
            </div>
          </div>
          
          <div class="section-card">
            <div class="section-header">
              <h2>Recent Trades</h2>
              <router-link to="/trade" class="view-all">View All</router-link>
            </div>
            
            <div class="recent-trades">
              <div 
                v-for="trade in recentTrades" 
                :key="trade.id"
                class="trade-item"
              >
                <div class="trade-token">
                  <div class="token-icon small">{{ trade.symbol.charAt(0) }}</div>
                  <div class="token-details">
                    <div class="token-symbol">{{ trade.symbol }}</div>
                    <div class="trade-time">{{ trade.time }}</div>
                  </div>
                </div>
                <div class="trade-details">
                  <div 
                    class="trade-type"
                    :class="{ 'buy': trade.type === 'buy', 'sell': trade.type === 'sell' }"
                  >
                    {{ trade.type.toUpperCase() }}
                  </div>
                  <div class="trade-amount">{{ trade.amount }} {{ trade.symbol }}</div>
                  <div class="trade-price">{{ formatUSD(trade.price) }}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <div class="dashboard-column">
          <div class="section-card">
            <div class="section-header">
              <h2>Watchlist</h2>
              <router-link to="/markets" class="view-all">View Markets</router-link>
            </div>
            
            <div class="watchlist">
              <TokenRow 
                v-for="token in watchlist" 
                :key="token.symbol"
                :token="token"
                :show-chart="true"
                :clickable="true"
                @click="navigateToTrade"
              />
              
              <div v-if="watchlist.length === 0" class="empty-watchlist">
                <p>Your watchlist is empty</p>
                <router-link to="/markets" class="add-tokens-button">
                  Add tokens
                </router-link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import AppNavbar from '@/components/AppNavbar.vue';
import TokenRow from '@/components/TokenRow.vue';

const router = useRouter();

// Dashboard data (would come from API/store in a real app)
const lastUpdated = ref(new Date());
const totalBalance = ref(24563.89);
const portfolioChange = ref(5.32);
const selectedPeriod = ref('1W');

// Sample data
const periods = ['1D', '1W', '1M', '3M', '1Y', 'ALL'];
const assets = ref([
  { symbol: 'BTC', name: 'Bitcoin', balance: 0.5, price: 35000, value: 17500, percentage: 71.2 },
  { symbol: 'ETH', name: 'Ethereum', balance: 2.5, price: 2000, value: 5000, percentage: 20.4 },
  { symbol: 'SOL', name: 'Solana', balance: 15, price: 80, value: 1200, percentage: 4.9 },
  { symbol: 'LINK', name: 'Chainlink', balance: 50, price: 15, value: 750, percentage: 3.1 },
  { symbol: 'USDT', name: 'Tether', balance: 113.89, price: 1, value: 113.89, percentage: 0.4 }
]);

const watchlist = ref([
  { symbol: 'BTC', name: 'Bitcoin', price: 35000, priceChange: 2.4, volume: 28000000000 },
  { symbol: 'ETH', name: 'Ethereum', price: 2000, priceChange: 3.8, volume: 12000000000 },
  { symbol: 'SOL', name: 'Solana', price: 80, priceChange: 5.2, volume: 3500000000 },
  { symbol: 'LINK', name: 'Chainlink', price: 15, priceChange: -1.2, volume: 950000000 },
  { symbol: 'ADA', name: 'Cardano', price: 0.45, priceChange: -0.8, volume: 750000000 }
]);

const recentTrades = ref([
  { id: 1, symbol: 'BTC', type: 'buy', amount: 0.05, price: 34850, time: '10 mins ago' },
  { id: 2, symbol: 'ETH', type: 'sell', amount: 1.2, price: 2025, time: '2 hours ago' },
  { id: 3, symbol: 'SOL', type: 'buy', amount: 5, price: 78.5, time: '1 day ago' }
]);

// Computed properties
const formattedDate = computed(() => {
  return lastUpdated.value.toLocaleString();
});

const topAssets = computed(() => {
  return [...assets.value].sort((a, b) => b.value - a.value);
});

const assetValues = computed(() => {
  return topAssets.value.map(asset => asset.value);
});

const assetLabels = computed(() => {
  return topAssets.value.map(asset => asset.symbol);
});

const chartStartDate = computed(() => {
  const date = new Date();
  
  switch(selectedPeriod.value) {
    case '1D': date.setDate(date.getDate() - 1); break;
    case '1W': date.setDate(date.getDate() - 7); break;
    case '1M': date.setMonth(date.getMonth() - 1); break;
    case '3M': date.setMonth(date.getMonth() - 3); break;
    case '1Y': date.setFullYear(date.getFullYear() - 1); break;
    case 'ALL': date.setFullYear(2020); break;
  }
  
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
});

const chartEndDate = computed(() => {
  return new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
});

// Generate random portfolio historical data
const generateChartData = () => {
  let data = [];
  const endDate = new Date();
  let currentDate = new Date();
  
  switch(selectedPeriod.value) {
    case '1D':
      currentDate.setDate(currentDate.getDate() - 1);
      for (let i = 0; i <= 24; i++) {
        const timestamp = new Date(currentDate.getTime() + (i * 60 * 60 * 1000));
        const value = 22000 + Math.random() * 5000;
        data.push([timestamp.getTime(), value]);
      }
      break;
    case '1W':
      currentDate.setDate(currentDate.getDate() - 7);
      for (let i = 0; i <= 7; i++) {
        const timestamp = new Date(currentDate.getTime() + (i * 24 * 60 * 60 * 1000));
        const value = 21000 + Math.random() * 7000;
        data.push([timestamp.getTime(), value]);
      }
      break;
    case '1M':
      currentDate.setMonth(currentDate.getMonth() - 1);
      for (let i = 0; i <= 30; i++) {
        const timestamp = new Date(currentDate.getTime() + (i * 24 * 60 * 60 * 1000));
        const value = 20000 + Math.random() * 8000;
        data.push([timestamp.getTime(), value]);
      }
      break;
    case '3M':
      currentDate.setMonth(currentDate.getMonth() - 3);
      for (let i = 0; i <= 90; i += 3) {
        const timestamp = new Date(currentDate.getTime() + (i * 24 * 60 * 60 * 1000));
        const value = 19000 + Math.random() * 10000;
        data.push([timestamp.getTime(), value]);
      }
      break;
    case '1Y':
      currentDate.setFullYear(currentDate.getFullYear() - 1);
      for (let i = 0; i <= 12; i++) {
        const timestamp = new Date(currentDate.getTime());
        timestamp.setMonth(currentDate.getMonth() + i);
        const value = 18000 + Math.random() * 12000;
        data.push([timestamp.getTime(), value]);
      }
      break;
    case 'ALL':
      currentDate.setFullYear(2020);
      for (let i = 0; i <= 4 * 12; i += 3) {
        const timestamp = new Date(currentDate.getTime());
        timestamp.setMonth(currentDate.getMonth() + i);
        let value = 10000 + (i * 800) + (Math.random() * 5000 - 2500);
        if (value < 5000) value = 5000;
        data.push([timestamp.getTime(), value]);
      }
      break;
  }
  
  return data;
};

// Chart options
const pieChartOptions = computed(() => ({
  chart: {
    type: 'donut',
    background: 'transparent',
    foreColor: '#788195'
  },
  colors: ['#1347e4', '#13b9e4', '#5b30e5', '#f0b90b', '#e74c3c'],
  labels: assetLabels.value,
  legend: {
    position: 'bottom',
    horizontalAlign: 'center',
    fontSize: '14px',
    labels: {
      colors: '#788195'
    }
  },
  dataLabels: {
    enabled: false
  },
  plotOptions: {
    pie: {
      donut: {
        size: '65%'
      }
    }
  },
  tooltip: {
    theme: 'dark',
    y: {
      formatter: (value) => formatUSD(value)
    }
  },
  states: {
    hover: {
      filter: {
        type: 'none'
      }
    }
  }
}));

const areaChartOptions = {
  chart: {
    type: 'area',
    height: 280,
    toolbar: {
      show: false
    },
    animations: {
      enabled: true,
      easing: 'easeinout',
      speed: 800
    },
    background: 'transparent',
    foreColor: '#788195'
  },
  colors: ['#1347e4'],
  dataLabels: {
    enabled: false
  },
  stroke: {
    curve: 'smooth',
    width: 2
  },
  fill: {
    type: 'gradient',
    gradient: {
      shadeIntensity: 1,
      opacityFrom: 0.3,
      opacityTo: 0.1,
      stops: [0, 90, 100]
    }
  },
  grid: {
    borderColor: '#252f3d',
    strokeDashArray: 3,
    xaxis: {
      lines: {
        show: true
      }
    },
    yaxis: {
      lines: {
        show: true
      }
    }
  },
  xaxis: {
    type: 'datetime',
    labels: {
      style: {
        colors: '#788195'
      }
    },
    axisBorder: {
      show: false
    },
    axisTicks: {
      show: false
    }
  },
  yaxis: {
    labels: {
      style: {
        colors: '#788195'
      },
      formatter: (value) => formatUSD(value)
    }
  },
  tooltip: {
    theme: 'dark',
    x: {
      format: 'MMM dd, yyyy'
    },
    y: {
      formatter: (value) => formatUSD(value)
    }
  }
};

const areaSeries = computed(() => {
  return [
    {
      name: 'Portfolio Value',
      data: generateChartData()
    }
  ];
});

// Methods
const refreshData = () => {
  lastUpdated.value = new Date();
  // In a real app, this would fetch fresh data from an API
};

const formatUSD = (value, decimals = 2) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals
  }).format(value);
};

const formatPercentage = (percentage) => {
  const sign = percentage > 0 ? '+' : '';
  return `${sign}${percentage.toFixed(2)}%`;
};

const getAssetColor = (index) => {
  const colors = ['#1347e4', '#13b9e4', '#5b30e5', '#f0b90b', '#e74c3c'];
  return colors[index % colors.length];
};

const getSegmentStart = (index) => {
  if (index === 0) return 0;
  
  let sum = 0;
  for (let i = 0; i < index; i++) {
    sum += topAssets.value[i].percentage;
  }
  
  return sum;
};

const getSegmentEnd = (index) => {
  let sum = 0;
  for (let i = 0; i <= index; i++) {
    sum += topAssets.value[i].percentage;
  }
  
  return sum;
};

const navigateToTrade = (token) => {
  router.push(`/trade/${token.symbol}`);
};

// Update chart when period changes
const updateChartData = () => {
  areaSeries.value = [
    {
      name: 'Portfolio Value',
      data: generateChartData()
    }
  ];
};

// Watch for period changes
import { watch } from 'vue';
watch(selectedPeriod, () => {
  updateChartData();
});
</script>

<style scoped>
.dashboard {
  min-height: 100vh;
  background-color: #0a0e17;
  color: #fff;
}

.dashboard-content {
  padding-top: 84px;
  padding-left: 20px;
  padding-right: 20px;
  padding-bottom: 20px;
  max-width: 1200px;
  margin: 0 auto;
}

.dashboard-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
}

h1 {
  font-size: 24px;
  margin: 0;
  font-weight: 500;
}

.last-updated {
  color: #788195;
  font-size: 14px;
  display: flex;
  align-items: center;
}

.refresh-button {
  background: none;
  border: none;
  color: #1347e4;
  cursor: pointer;
  margin-left: 8px;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.2s;
}

.refresh-button:hover {
  transform: rotate(45deg);
}

.portfolio-summary {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  margin-bottom: 24px;
}

.summary-card {
  background-color: #171d28;
  border-radius: 8px;
  padding: 20px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  transition: transform 0.3s, box-shadow 0.3s;
}

.summary-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.15);
}

h2 {
  font-size: 18px;
  margin: 0 0 16px;
  font-weight: 500;
  color: #fff;
}

.balance-amount {
  font-size: 28px;
  font-weight: bold;
  margin-bottom: 8px;
}

.balance-change {
  font-size: 16px;
}

.balance-change.positive {
  color: #00c582;
}

.balance-change.negative {
  color: #e74c3c;
}

.allocation-chart {
  height: 200px;
  position: relative;
}

.dashboard-row {
  display: grid;
  grid-template-columns: 1.5fr 1fr;
  gap: 20px;
}

.dashboard-column {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.section-card {
  background-color: #171d28;
  border-radius: 8px;
  padding: 20px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}

.period-selector {
  display: flex;
  border-radius: 4px;
  overflow: hidden;
  background-color: #1a2334;
}

.period-button {
  background: none;
  border: none;
  color: #788195;
  padding: 6px 12px;
  cursor: pointer;
  font-size: 14px;
  transition: background-color 0.2s, color 0.2s;
}

.period-button:hover {
  color: #fff;
}

.period-button.active {
  background-color: #1347e4;
  color: white;
}

.portfolio-chart {
  height: 280px;
}

.view-all {
  color: #1347e4;
  text-decoration: none;
  font-size: 14px;
  transition: color 0.2s;
}

.view-all:hover {
  color: #13b9e4;
  text-decoration: underline;
}

.recent-trades {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.trade-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px;
  border-radius: 6px;
  background-color: #1a2334;
  transition: background-color 0.2s;
}

.trade-item:hover {
  background-color: #252f3d;
}

.trade-token {
  display: flex;
  align-items: center;
}

.token-icon {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background-color: #1347e4;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  margin-right: 12px;
}

.token-icon.small {
  width: 32px;
  height: 32px;
  font-size: 14px;
}

.token-details {
  display: flex;
  flex-direction: column;
}

.token-symbol {
  font-weight: 500;
}

.trade-time {
  font-size: 12px;
  color: #788195;
}

.trade-details {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
}

.trade-type {
  font-size: 12px;
  font-weight: 500;
  padding: 2px 8px;
  border-radius: 4px;
  margin-bottom: 4px;
}

.trade-type.buy {
  background-color: rgba(0, 197, 130, 0.1);
  color: #00c582;
}

.trade-type.sell {
  background-color: rgba(231, 76, 60, 0.1);
  color: #e74c3c;
}

.trade-amount {
  font-size: 14px;
  margin-bottom: 2px;
}

.trade-price {
  font-size: 12px;
  color: #788195;
}

.watchlist {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.empty-watchlist {
  text-align: center;
  padding: 30px 0;
  color: #788195;
}

.add-tokens-button {
  display: inline-block;
  margin-top: 12px;
  padding: 8px 16px;
  background-color: #1347e4;
  color: white;
  border-radius: 4px;
  text-decoration: none;
  transition: background-color 0.2s;
}

.add-tokens-button:hover {
  background-color: #0d3ab8;
}

@media (max-width: 1024px) {
  .portfolio-summary {
    grid-template-columns: 1fr 1fr;
    gap: 16px;
  }
  
  .dashboard-row {
    grid-template-columns: 1.2fr 0.8fr;
    gap: 16px;
  }
}

@media (max-width: 768px) {
  .dashboard-content {
    padding-top: 84px;
    padding-left: 16px;
    padding-right: 16px;
    padding-bottom: 16px;
  }
  
  .dashboard-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }
  
  .portfolio-summary,
  .dashboard-row {
    grid-template-columns: 1fr;
    gap: 16px;
  }
  
  .summary-card {
    padding: 16px;
  }
  
  .section-card {
    padding: 16px;
  }
  
  .section-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }
  
  .period-selector {
    width: 100%;
    overflow-x: auto;
    white-space: nowrap;
    padding-bottom: 8px;
  }
  
  .period-button {
    min-width: 40px;
  }
  
  .allocation-chart {
    height: 250px;
  }
  
  .portfolio-chart {
    height: 250px;
  }
}

@media (max-width: 480px) {
  .dashboard-content {
    padding-top: 74px;
    padding-left: 12px;
    padding-right: 12px;
    padding-bottom: 12px;
  }
  
  h1 {
    font-size: 20px;
  }
  
  h2 {
    font-size: 16px;
  }
  
  .balance-amount {
    font-size: 24px;
  }
  
  .balance-change {
    font-size: 14px;
  }
  
  .portfolio-summary,
  .dashboard-row {
    gap: 12px;
  }
  
  .allocation-chart {
    height: 200px;
  }
  
  .portfolio-chart {
    height: 220px;
  }
  
  .trade-item {
    padding: 10px;
  }
  
  .token-icon.small {
    width: 28px;
    height: 28px;
    font-size: 12px;
  }
  
  .token-symbol, .trade-time, .trade-price {
    font-size: 12px;
  }
  
  .trade-amount {
    font-size: 13px;
  }
  
  .trade-type {
    font-size: 10px;
    padding: 2px 6px;
  }
}
</style> 